import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UserPlus, Users, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useOwnerStore } from '@/lib/owner-store';
import type { TaskOwner } from '@/lib/types';

export function AdminPage() {
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState<TaskOwner | null>(null);
  const [ownerData, setOwnerData] = useState({
    name: '',
    email: '',
    organization: ''
  });

  const { owners, fetchOwners, addOwner, updateOwner, deleteOwner } = useOwnerStore();

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      if (editingOwner) {
        await updateOwner(editingOwner.id, ownerData);
        toast.success('Owner updated successfully');
      } else {
        await addOwner(ownerData);
        toast.success('Owner added successfully');
      }
      setOwnerData({ name: '', email: '', organization: '' });
      setEditingOwner(null);
      setDialogOpen(false);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (owner: TaskOwner) => {
    setEditingOwner(owner);
    setOwnerData({
      name: owner.name,
      email: owner.email,
      organization: owner.organization
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this owner?')) return;
    try {
      setLoading(true);
      await deleteOwner(id);
      toast.success('Owner deleted successfully');
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingOwner(null);
    setOwnerData({ name: '', email: '', organization: '' });
    setDialogOpen(true);
  };

  return (
    <div className="max-w-7xl mx-auto py-6 space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-semibold">Team Members</h2>
          </div>
          <Button onClick={handleAddNew} className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Add Member
          </Button>
        </div>
        
        <div className="p-4 space-y-4">
          {owners.map((owner) => (
            <div 
              key={owner.id} 
              className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-700"
            >
              <div>
                <h3 className="font-medium dark:text-gray-100">{owner.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{owner.email}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{owner.organization}</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(owner)}
                  disabled={loading}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(owner.id)}
                  disabled={loading}
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[425px] bg-white dark:bg-gray-800">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {editingOwner ? 'Edit Team Member' : 'Add New Team Member'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Input
                placeholder="Full Name"
                value={ownerData.name}
                onChange={(e) => setOwnerData({ ...ownerData, name: e.target.value })}
                required
                className="bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600"
              />
            </div>
            <div>
              <Input
                type="email"
                placeholder="Email"
                value={ownerData.email}
                onChange={(e) => setOwnerData({ ...ownerData, email: e.target.value })}
                required
                className="bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600"
              />
            </div>
            <div>
              <Input
                placeholder="Organization"
                value={ownerData.organization}
                onChange={(e) => setOwnerData({ ...ownerData, organization: e.target.value })}
                required
                className="bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600"
              />
            </div>
            <Button type="submit" disabled={isSubmitting} className="w-full">
              {isSubmitting ? 'Saving...' : editingOwner ? 'Update Member' : 'Add Member'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
